import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    console.log('[Cart] Quantity change event received:', { quantity, line, event });

    if (!line) {
      console.warn('[Cart] No line number provided in quantity change event');
      return;
    }

    if (quantity === 0) {
      console.log('[Cart] Quantity is 0, removing line item:', line);
      return this.onLineItemRemove(line);
    }

    console.log('[Cart] Calling updateQuantity with:', { line, quantity, action: 'change' });

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Handles the line item removal by key (for personalization products).
   * @param {string} itemKey - The item key.
   */
  async onLineItemRemoveByKey(itemKey) {
    // Find the row element
    const cartItemRowToRemove = this.refs.cartItemRows.find((row) => row.dataset.key === itemKey);

    if (!cartItemRowToRemove) {
      console.warn('Cart item row not found for key:', itemKey);
      return;
    }

    // Check if this item has a personalisation GUID
    const personalisationGuid = cartItemRowToRemove.dataset.personalisationGuid;

    // Build the updates object
    const updates = {};
    updates[itemKey] = 0;

    // If this item has personalisation, find and remove the linked personalisation product
    if (personalisationGuid) {
      try {
        // Fetch current cart to find the personalisation product
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        console.log('Looking for personalisation product with parent GUID:', personalisationGuid);

        // Find the personalisation product with matching parent GUID
        const personalisationItem = cart.items.find((item) => {
          if (item.properties) {
            // Properties can be an object or array
            const hasParentGuid = item.properties._personalisation_parent_guid === personalisationGuid ||
              (Array.isArray(item.properties) &&
               item.properties.some(prop => prop.name === '_personalisation_parent_guid' && prop.value === personalisationGuid));
            if (hasParentGuid) {
              console.log('Found personalisation product to remove:', item.key);
            }
            return hasParentGuid;
          }
          return false;
        });

        if (personalisationItem) {
          updates[personalisationItem.key] = 0;
          console.log('Will remove both items:', Object.keys(updates));
        }
      } catch (error) {
        console.error('Error fetching cart to find personalisation product:', error);
      }
    }

    // Use updateQuantityByKey to remove the items
    await this.updateQuantityByKey(updates);

    // Handle row removal animations
    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Check if this item has a personalisation GUID
    // Note: We can't use line index to find rows because hidden personalization products
    // cause a mismatch between Shopify's line numbers and visible row indices.
    // We need to fetch the cart first to get the item key, then find the matching row.

    console.log('[Cart] Fetching cart to find item at line:', line);

    let itemRow = null;
    let itemKey = null;
    let personalisationGuid = null;

    try {
      // Fetch current cart to get the item key for this line
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const cartItem = cart.items[line - 1]; // Shopify's line numbers are 1-indexed

      if (cartItem) {
        itemKey = cartItem.key;
        console.log('[Cart] Found cart item at line', line, 'with key:', itemKey);

        // Now find the matching DOM row by key
        itemRow = this.refs.cartItemRows?.find(row => row?.dataset.key === itemKey);

        // If refs are stale, query the DOM
        if (!itemRow) {
          console.log('[Cart] Refs stale, querying DOM for row with key:', itemKey);
          itemRow = this.querySelector(`tr.cart-items__table-row[data-key="${itemKey}"]`);
        }

        if (itemRow) {
          personalisationGuid = itemRow.dataset.personalisationGuid;
        }
      }
    } catch (error) {
      console.error('[Cart] Error fetching cart to find item:', error);
    }

    console.log('[Cart] Checking for personalization:', {
      line,
      itemKey,
      personalisationGuid,
      hasItemRow: !!itemRow,
      itemRowDataset: itemRow?.dataset,
      method: this.refs.cartItemRows?.[line - 1] ? 'refs' : 'DOM query'
    });

    // If item has personalisation, use key-based updates to sync quantities
    if (itemKey && personalisationGuid) {
      console.log('[Cart] Item has personalization, fetching cart for sync...');
      try {
        // Fetch current cart to find the personalisation product
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        console.log('Updating quantity for item with personalisation GUID:', personalisationGuid);

        // Find the personalisation product with matching parent GUID
        const personalisationItem = cart.items.find((item) => {
          if (item.properties) {
            // Properties can be an object or array
            const hasParentGuid = item.properties._personalisation_parent_guid === personalisationGuid ||
              (Array.isArray(item.properties) &&
               item.properties.some(prop => prop.name === '_personalisation_parent_guid' && prop.value === personalisationGuid));
            return hasParentGuid;
          }
          return false;
        });

        if (personalisationItem) {
          console.log('[Cart] Found personalization item:', personalisationItem);

          // Update both items using key-based update
          const updates = {};
          updates[itemKey] = quantity;
          updates[personalisationItem.key] = quantity;

          console.log('[Cart] Updating both items to quantity:', quantity, 'Updates:', updates);

          const body = JSON.stringify({
            updates: updates,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          });

          cartTotal?.shimmer();

          try {
            const response = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }));
            const responseText = await response.text();
            const parsedResponseText = JSON.parse(responseText);

            resetShimmer(this);

            if (parsedResponseText.errors) {
              this.#handleCartError(line, parsedResponseText);
              return;
            }

            const newSectionHTML = new DOMParser().parseFromString(
              parsedResponseText.sections[this.sectionId],
              'text/html'
            );

            const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
            const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

            this.dispatchEvent(
              new CartUpdateEvent({}, this.sectionId, {
                itemCount: newCartItemCount,
                source: 'cart-items-component',
                sections: parsedResponseText.sections,
              })
            );

            morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
            console.log('[Cart] Personalization sync complete, returning early');
          } catch (error) {
            console.error('[Cart] Error updating cart with personalization:', error);
          } finally {
            this.#enableCartItems();
            cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
          }
          return;
        } else {
          console.warn('[Cart] No personalization item found for GUID:', personalisationGuid);
        }
      } catch (error) {
        console.error('[Cart] Error fetching cart for personalization update:', error);
        // Fall through to regular update if there's an error
      }
    } else {
      console.log('[Cart] No personalization detected, using regular update');
    }

    // Regular update without personalization
    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates quantities by item keys (for personalization products).
   * @param {Object} updates - Object mapping item keys to quantities.
   * @returns {Promise<void>}
   */
  async updateQuantityByKey(updates) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      updates: updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    try {
      const response = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponseText.errors) {
        console.error('Cart update error:', parsedResponseText.errors);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
    } catch (error) {
      console.error('Error updating cart by key:', error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Validates and corrects personalization quantities after cart updates.
   * Ensures that personalization products always have the same quantity as their main products.
   * @returns {Promise<void>}
   */
  async #validatePersonalisationQuantities() {
    try {
      // Fetch current cart state
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Build a map of GUIDs to personalization products
      const personalisationMap = new Map();

      // Find all personalization child products
      cart.items.forEach((item) => {
        if (item.properties) {
          const parentGuid = item.properties._personalisation_parent_guid ||
            (Array.isArray(item.properties) &&
             item.properties.find(prop => prop.name === '_personalisation_parent_guid')?.value);

          if (parentGuid) {
            personalisationMap.set(parentGuid, {
              key: item.key,
              quantity: item.quantity,
            });
          }
        }
      });

      // Check main products with personalization
      const updates = {};
      let needsSync = false;

      cart.items.forEach((item) => {
        if (item.properties) {
          const guid = item.properties._personalisation_guid ||
            (Array.isArray(item.properties) &&
             item.properties.find(prop => prop.name === '_personalisation_guid')?.value);

          if (guid) {
            const personalisationItem = personalisationMap.get(guid);

            // Check if quantities match
            if (personalisationItem && personalisationItem.quantity !== item.quantity) {
              console.warn(
                `Quantity mismatch detected: Main product (${item.key}) has quantity ${item.quantity}, ` +
                  `but personalization product has quantity ${personalisationItem.quantity}. Syncing...`
              );

              // Sync to main product's quantity
              updates[personalisationItem.key] = item.quantity;
              needsSync = true;
            }
          }
        }
      });

      // Apply corrections if needed
      if (needsSync) {
        console.log('Syncing personalization quantities:', updates);
        await this.updateQuantityByKey(updates);
      }
    } catch (error) {
      console.error('Error validating personalization quantities:', error);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      await this.#validatePersonalisationQuantities();
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      await this.#validatePersonalisationQuantities();
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      await this.#validatePersonalisationQuantities();
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
